import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DaterangepickerDirective } from 'ngx-daterangepicker-material';
import { Moment } from 'moment-timezone';
import * as moment from 'moment';
import { AlertService, CampaignService, GatewayDashboardService } from '../../_services';
import { Campaign, DashboardDateRanges, GatewayDashboardSections, Pager } from '../../_models';
import { FilterTopBarEntry, GatewayCountType, GatewayFilterPayload, GatewayTransactionType } from '../gateway-dashboard/gateway-summary';


@Component({
  selector: 'gateway-filter-bar',
  templateUrl: './gateway-filter-bar.component.html',
  styleUrls: ['./gateway-filter-bar.component.scss'],
})
export class GatewayFilterBarComponent implements OnInit, OnDestroy {
  @ViewChild('datePickerInput', { read: DaterangepickerDirective, static: false })
  pickerDirective: DaterangepickerDirective;

  @ViewChild('comparePickerInput', { read: DaterangepickerDirective, static: false })
  comparePickerDirective: DaterangepickerDirective;

  @Input() selectedSection: GatewayDashboardSections = GatewayDashboardSections.TransactionSummary;

  @Output() applyFilters = new EventEmitter<GatewayFilterPayload>();

  get isApprovalDashboard(): boolean {
    return this.selectedSection === GatewayDashboardSections.ApprovalAnalytics;
  }

  campaigns: Campaign[] = [];
  merchantOptions: FilterTopBarEntry[] = [];
  merchantsLoading = false;

  orderTypeOptions: { value: GatewayTransactionType; label: string }[] = [
    { value: GatewayTransactionType.Initial, label: 'Initial Orders' },
    { value: GatewayTransactionType.Rebill, label: 'Rebills' },
    { value: GatewayTransactionType.OneTime, label: 'One-Time Orders' },
  ];

  countTypeOptions: { value: GatewayCountType; label: string }[] = [
    { value: GatewayCountType.Gross, label: 'Gross' },
    { value: GatewayCountType.Unique, label: 'Unique' },
  ];

  date: { startDate: Moment; endDate: Moment };
  compareDate: { startDate: Moment; endDate: Moment };
  maxDate: Moment;

  pendingFilters: GatewayFilterPayload = {
    start_date: moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    end_date: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    compare_start_date: moment().subtract(14, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    compare_end_date: moment().subtract(8, 'days').endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  };

  public ranges = DashboardDateRanges;

  protected _destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private campaignService: CampaignService,
    private gatewayDashboardService: GatewayDashboardService,
    private alertService: AlertService
  ) {}

  ngOnInit() {
    this.maxDate = moment();
    this.date = {
      startDate: moment().subtract(7, 'days').startOf('day'),
      endDate: moment().endOf('day'),
    };
    this.compareDate = {
      startDate: moment().subtract(14, 'days').startOf('day'),
      endDate: moment().subtract(8, 'days').endOf('day'),
    };
    this.getCampaigns();
    this.getMerchants();
  }

  getCampaigns() {
    this.campaignService
      .list({ is_hybrid: false })
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (result: Pager) => {
          this.campaigns = result.results;
        },
        (error) => {
          this.alertService.error(error.message);
        }
      );
  }

  getMerchants() {
    this.merchantsLoading = true;
    this.gatewayDashboardService
      .getFilterOptions()
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (response) => {
          this.merchantsLoading = false;
          if (response && response.top_bar && response.top_bar.gateways) {
            this.merchantOptions = response.top_bar.gateways;
          }
        },
        (error) => {
          this.merchantsLoading = false;
          this.alertService.error(error.message);
        }
      );
  }

  openDatepicker() {
    if (this.comparePickerDirective) {
      this.comparePickerDirective.hide();
    }
    this.pickerDirective.open();
  }

  openCompareDatepicker() {
    if (this.pickerDirective) {
      this.pickerDirective.hide();
    }
    this.comparePickerDirective.open();
  }

  onDateChange = (dateRange: { startDate: Moment; endDate: Moment }) => {
    if (dateRange.startDate && dateRange.endDate) {
      this.pendingFilters = {
        ...this.pendingFilters,
        start_date: dateRange.startDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        end_date: dateRange.endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      };
      this.onApply();
    }
  };

  onCompareDateChange = (dateRange: { startDate: Moment; endDate: Moment }) => {
    if (dateRange.startDate && dateRange.endDate) {
      this.pendingFilters = {
        ...this.pendingFilters,
        compare_start_date: dateRange.startDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        compare_end_date: dateRange.endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      };
      this.onApply();
    }
  };

  onMerchantChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.pendingFilters = { ...this.pendingFilters, merchant_id: Number(value) };
  }

  onCampaignChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.pendingFilters = { ...this.pendingFilters, campaign_id: value };
  }

  onOrderTypeChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.pendingFilters = { ...this.pendingFilters, order_type: value };
  }

  onCountTypeChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value as GatewayCountType;
    this.pendingFilters = { ...this.pendingFilters, count_type: value || undefined };
  }

  onApply() {
    const payload: GatewayFilterPayload = { ...this.pendingFilters };
    if (this.isApprovalDashboard) {
      delete payload.campaign_id;
      delete payload.count_type;
    }
    this.applyFilters.emit(payload);
  }

  ngOnDestroy() {
    this._destroy$.next(true);
    this._destroy$.complete();
  }
}
